import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { secretsApi } from '@/lib/api/secrets';
import { SecretResponse, SecretType, SecretMode } from '@optidev/shared-types';
import toast from 'react-hot-toast';
import { Link } from 'lucide-react';

interface EditSecretModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  secret: SecretResponse | null;
}

export default function EditSecretModal({
  isOpen,
  onClose,
  onSuccess,
  secret,
}: EditSecretModalProps) {
  const { getIdToken } = useAuth();
  const [loading, setLoading] = useState(false);

  const [key, setKey] = useState('');
  const [type, setType] = useState<SecretType>(SecretType.MASKED);
  const [baseValue, setBaseValue] = useState('');
  const [devValue, setDevValue] = useState('');
  const [stagingValue, setStagingValue] = useState('');
  const [prodValue, setProdValue] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (secret) {
      setKey(secret.key);
      setType(secret.type);
      setBaseValue(secret.values.base || '');
      setDevValue(secret.values.development || '');
      setStagingValue(secret.values.staging || '');
      setProdValue(secret.values.production || '');
      setDescription(secret.description || '');
    }
  }, [secret]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!secret) return;

    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const values =
        secret.mode === SecretMode.LITE
          ? { base: baseValue }
          : { base: baseValue, development: devValue, staging: stagingValue, production: prodValue };

      await secretsApi.update(token, secret.id, {
        key,
        type,
        values,
        description,
      });

      onSuccess();
    } catch (error: any) {
      console.error('Failed to update secret:', error);
      toast.error(error.response?.data?.message || 'Failed to update secret');
    } finally {
      setLoading(false);
    }
  };

  if (!secret) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Secret</DialogTitle>
        </DialogHeader>

        {secret.isLinked ? (
          <div className="space-y-4">
            <div className="border rounded-md p-4">
              <div className="flex items-start gap-3">
                <Link className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-medium mb-1">
                    Linked Secret
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    This secret is linked from your Account-level secrets. Changes to the account secret will automatically cascade to all linked projects.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    To customize this secret for this project only, you must first unlink it.
                  </p>
                </div>
              </div>
            </div>

            <div className="border rounded-md p-4 space-y-3">
              <div>
                <Label className="text-sm font-medium">Key</Label>
                <div className="mt-1 font-mono text-sm">{secret.key}</div>
              </div>
              {secret.description && (
                <div>
                  <Label className="text-sm font-medium">Description</Label>
                  <div className="mt-1 text-sm">{secret.description}</div>
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="key">Key</Label>
              <Input id="key" value={key} onChange={(e) => setKey(e.target.value)} maxLength={255} />
            </div>

            <div>
              <Label htmlFor="type">Type</Label>
              <Select value={type} onValueChange={(value) => setType(value as SecretType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SecretType.VISIBLE}>Visible (plaintext)</SelectItem>
                  <SelectItem value={SecretType.MASKED}>Masked (encrypted, revealable)</SelectItem>
                  <SelectItem value={SecretType.SENSITIVE}>Sensitive (never revealable)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="baseValue">Base Value</Label>
              <Textarea
                id="baseValue"
                value={baseValue}
                onChange={(e) => setBaseValue(e.target.value)}
                rows={3}
              />
            </div>

            {secret.mode === SecretMode.POWER && (
              <>
                <div>
                  <Label htmlFor="devValue">Development Value</Label>
                  <Input
                    id="devValue"
                    value={devValue}
                    onChange={(e) => setDevValue(e.target.value)}
                    placeholder="Leave empty to inherit from Base"
                  />
                </div>

                <div>
                  <Label htmlFor="stagingValue">Staging Value</Label>
                  <Input
                    id="stagingValue"
                    value={stagingValue}
                    onChange={(e) => setStagingValue(e.target.value)}
                    placeholder="Leave empty to inherit from Base"
                  />
                </div>

                <div>
                  <Label htmlFor="prodValue">Production Value</Label>
                  <Input
                    id="prodValue"
                    value={prodValue}
                    onChange={(e) => setProdValue(e.target.value)}
                    placeholder="Leave empty to inherit from Base"
                  />
                </div>
              </>
            )}

            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={1000}
              />
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Updating...' : 'Update Secret'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}